// Composition helper for the lead workspace's GHL-send surface. Extracted
// from `useLeadWorkspaceShellProps` (slice B3 of the v1.0 Lead Control
// MVP polish pass) to keep the shell composition root under the
// file-size hygiene soft cap and to give future Lead-Control slices
// (B2 reactive activity dock, GHL opportunity creation, etc.) a single
// place to evolve send-related behavior.
//
// Owns:
//   - `useResolveCrmLinkUrl` wiring (per-org/per-location CRM deep
//     link surfaced in the post-send toast).
//   - `useGhlSender` invocation.
//   - `sendAllUnsent` bulk-send for the dock's "Send all" button.
//
// Returns the same shape the shell previously composed inline, so the
// caller's destructure stays the same.

import { useCallback } from "react";
import { speak } from "../../lib/speech/speak";
import { useGhlSender } from "../../hooks/useGhlSender";
import { useResolveCrmLinkUrl } from "../../hooks/useResolveCrmLinkUrl";
import { APP_VERSION } from "@/config/version";
import type { Lead } from "@/types/lead";
import type { Location } from "@/types/location";
import type { Salesperson, Toast } from "../../hooks/ghlSenderTypes";

export type UseLeadWorkspaceGhlSenderArgs = {
  leads: Lead[];
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
  currentLead: Lead | null;
  setCurrentLead: React.Dispatch<React.SetStateAction<Lead | null>>;
  location: Location | null;
  salesperson: Salesperson | null;
  webhookUrl: string;
  appDisplayName: string | undefined;
  scopedOrganizationId: string | null | undefined;
  accessToken: string | null | undefined;
  setToast: (t: Toast | null) => void;
  setLastSendAt: (ts: number | null) => void;
  setLastSendStatus: (s: "success" | "error" | null) => void;
  archiveLead: (leadId: string) => Promise<void>;
  /** Pre-filtered leads scoped to the active workspace location. */
  locationScopedLeads: Lead[];
};

export function useLeadWorkspaceGhlSender(args: UseLeadWorkspaceGhlSenderArgs) {
  const resolveCrmLinkUrl = useResolveCrmLinkUrl(
    args.scopedOrganizationId ?? null,
    args.location?.id ?? null,
  );

  const { sendToGHL, isSending, buildGhlPayload } = useGhlSender({
    leads: args.leads,
    setLeads: args.setLeads,
    currentLead: args.currentLead,
    setCurrentLead: args.setCurrentLead,
    location: args.location,
    salesperson: args.salesperson,
    webhookUrl: args.webhookUrl,
    version: APP_VERSION,
    appDisplayName: args.appDisplayName,
    orgId: args.scopedOrganizationId ?? null,
    accessToken: args.accessToken ?? null,
    setToast: args.setToast,
    setLastSendAt: args.setLastSendAt,
    setLastSendStatus: args.setLastSendStatus,
    speak,
    onLeadSent: args.archiveLead,
    resolveCrmLinkUrl,
  });

  // Slice D — bulk "Send all" for the dock's unsent-leads section.
  // Sequential (not parallel) so `useGhlSender`'s `sendLocksRef` keeps
  // one-lock-per-lead. Filters off `locationScopedLeads` so the bulk
  // action matches what the dock section renders.
  const sendAllUnsent = useCallback(async () => {
    for (const lead of args.locationScopedLeads.filter((l) => !l.sent && !l.isTest)) {
      await sendToGHL(lead);
    }
  }, [args.locationScopedLeads, sendToGHL]);

  return { sendToGHL, isSending, buildGhlPayload, sendAllUnsent };
}
